import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import cn from 'classnames';
import urljoin from 'url-join';

import Layout from '@/components/Layout/Vanilla';
import Container from '@/components/container';
import Heading from '@/components/Heading';
import { sdk } from '@/lib/graphcms';
import Badge from '@/components/blog/badge';
import CardsContainer from '@/components/blog/cards-container';
import FeaturedCaseStudy from '@/components/case-studies/featured-case-study';
import Pagination from '@/components/blog/pagination';
import Card from '@/components/case-studies/case-study-card';
import SlackCard from '@/components/blog/slack-card';
import EnterpriseCard from '@/components/blog/enterprise-card';
import styles from '@/styles/pages/blog-index.module.css';
import { resourceCards } from '@/utils/data/resource-cards';
import {
  SuccessStoriesPage,
  SuccessStoryCardFragment,
} from '@/generated/graphcms-schema';
import ResourceCard from '@/components/blog/resource-card';
import { getPagination } from '@/utils/blog';
import { defaultSeoUrl } from '../../../next-seo.config';

type CaseStudiesProps = SuccessStoriesPage & {
  preview: boolean;
  isFirstPage: boolean;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  totalCount: number;
  currentPage: number;
  featuredCaseStudy: SuccessStoryCardFragment;
  successStories: SuccessStoryCardFragment[];
};

// The limit is 10 because we remove the featured post from the list
const limit = 10;

export default function CaseStudies({
  totalCount,
  page,
  successStoryUseCases,
  isFirstPage,
  currentPage,
  hasPreviousPage,
  hasNextPage,
  preview,
  featuredCaseStudy,
  successStories,
}: CaseStudiesProps) {
  const router = useRouter();

  const pageUrl = urljoin(defaultSeoUrl, router.asPath);
  const pageTitle = !isFirstPage
    ? `Page ${currentPage} | ${page?.title}`
    : page?.title;

  return (
    <>
      <Head>
        {hasPreviousPage && (
          <link rel="prev" href={`/case-studies?page=${currentPage - 1}`} />
        )}
        {hasNextPage && (
          <link rel="next" href={`/case-studies?page=${currentPage + 1}`} />
        )}
        {!isFirstPage && <link rel="canonical" href={pageUrl} />}
      </Head>

      <Layout
        seo={{
          meta: {
            ...page?.seo,
            title: pageTitle,
          },
          title: page?.title || ``,
          subTitle: page?.subTitle,
          coverImage: page?.coverImage || {},
        }}
        preview={preview}
      >
        <Container>
          <Heading>Case Studies</Heading>

          {successStoryUseCases.length > 0 && (
            <div className="mt-6 space-y-3 lg:space-y-2">
              {successStoryUseCases.map((useCase) => (
                <Badge
                  key={useCase.slug}
                  slug={`/case-studies/use-case/${useCase.slug}`}
                  title={useCase.title}
                  variant="default"
                  className="mr-3 lg:mr-2"
                />
              ))}
            </div>
          )}

          <div className="mt-10">
            <FeaturedCaseStudy {...featuredCaseStudy} />
          </div>

          <CardsContainer className="mt-10 lg:mt-20">
            {successStories?.map((successStory) => (
              <Card key={successStory.slug} {...successStory} />
            ))}
          </CardsContainer>

          <div className="mt-20">
            <Pagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={limit}
              onPageChange={(pageNumber) => {
                router.push(`/case-studies?page=${pageNumber}`);
              }}
            />
          </div>

          <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {resourceCards.map((item) => (
              <ResourceCard key={item.href} {...item} />
            ))}
          </div>

          <div className={cn(`mt-20 mb-12 lg:mb-36`, styles.cardsFooter)}>
            <SlackCard />
            <EnterpriseCard />
          </div>
        </Container>
      </Layout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  preview = false,
  query,
  res,
}) => {
  const { page, offset } = getPagination({
    queryPage: query.page,
    limit,
  });

  const { page: caseStudyPage, successStoryUseCases } = await sdk({
    preview,
  }).successStoriesPage({
    slug: `case-studies`,
  });

  const totalCount = caseStudyPage?.successStories.length || 0;
  const isFirstPage = page === 1;
  const totalNumberOfPages = Math.ceil(totalCount / limit);

  if (page > totalNumberOfPages) {
    return {
      redirect: {
        destination: `/case-studies`,
        permanent: false,
      },
    };
  }

  const allSuccessStories = caseStudyPage?.successStories.slice(
    page * limit - limit,
    offset + limit,
  );

  const featuredCaseStudy = allSuccessStories && allSuccessStories[0];
  const successStories = allSuccessStories && allSuccessStories.slice(1);

  const hasNextPage = page < totalNumberOfPages;
  const hasPreviousPage = page > 1;

  res.setHeader(`Cache-Control`, `s-maxage=3600, stale-while-revalidate`);

  return {
    props: {
      totalCount,
      page: caseStudyPage,
      successStoryUseCases,
      isFirstPage,
      currentPage: page,
      hasPreviousPage,
      hasNextPage,
      preview,
      featuredCaseStudy,
      successStories,
    },
  };
};
